import { PREMIUM_TIERS } from './PremiumFilter';

/**
 * Gets the premium tier of a user, handling various data structures
 * @param {Object|string} user - User object or data that might contain premium information
 * @returns {number} - Premium tier (0-3)
 */
export const getUserPremiumTier = (user) => {
  // Handle null, undefined, or string values (like user IDs)
  if (!user || typeof user === 'string') {
    return PREMIUM_TIERS.NONE;
  }
  
  // Case 1: user is an object with premium property
  if (typeof user === 'object' && 'premium' in user) {
    return typeof user.premium === 'number' ? user.premium : PREMIUM_TIERS.NONE;
  }
  
  // Case 2: user is an object from API with nested data
  if (typeof user === 'object' && user.user && typeof user.user === 'object' && 'premium' in user.user) {
    return typeof user.user.premium === 'number' ? user.user.premium : PREMIUM_TIERS.NONE;
  }
  
  // Default: non-premium
  return PREMIUM_TIERS.NONE;
};

/**
 * Sorts an array of users by their premium status
 * Higher tiers (GOLD=3) appear first, then lower tiers, then non-premium users
 * 
 * @param {Array} users - Array of user objects containing premium information
 * @param {string} [userPropertyPath=''] - Path to user object if nested (e.g., 'user' if data is like {user: {premium: 2}})
 * @returns {Array} - Sorted array of users
 */
export const sortUsersByPremium = (users, userPropertyPath = '') => {
  if (!Array.isArray(users)) return [];
  
  return [...users].sort((a, b) => {
    // Extract the actual user objects based on property path
    const userA = userPropertyPath ? a[userPropertyPath] : a;
    const userB = userPropertyPath ? b[userPropertyPath] : b;
    
    // Get premium tiers
    const premiumA = getUserPremiumTier(userA);
    const premiumB = getUserPremiumTier(userB);
    
    // Sort by premium tier (higher first)
    return premiumB - premiumA;
  });
};

/**
 * Sorts a list of comments by the premium status of their creators
 * 
 * @param {Array} comments - Array of comment objects
 * @returns {Array} - Sorted array of comments
 */
export const sortCommentsByUserPremium = (comments) => {
  if (!Array.isArray(comments)) return [];
  
  return [...comments].sort((a, b) => {
    // Check for directly provided userPremium field first (from our enriched comments)
    if ('userPremium' in a && 'userPremium' in b) {
      if (a.userPremium !== b.userPremium) {
        return b.userPremium - a.userPremium;
      }
      
      // If premium status is the same, sort by likes count
      const aLikes = a.likes ? a.likes.length : 0;
      const bLikes = b.likes ? b.likes.length : 0;
      return bLikes - aLikes;
    }
    
    // Fall back to previous logic if userPremium isn't available
    const premiumA = getUserPremiumTier(a.userHandle ? { premium: a.userPremium } : a);
    const premiumB = getUserPremiumTier(b.userHandle ? { premium: b.userPremium } : b);
    
    if (premiumA !== premiumB) {
      return premiumB - premiumA;
    }
    
    // If premium tiers are the same, sort by likes count
    const aLikes = a.likes ? a.likes.length : 0;
    const bLikes = b.likes ? b.likes.length : 0;
    return bLikes - aLikes;
  });
}; 